import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FullscreenTakeover, QueryLoader } from "@jsq/ds/core";
import { globalEnv } from "~/global-env";
import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import { useGetProfileDetailsQuery } from "~/graphql/supergraphHooks";
import { useGuidedProfileCreation } from "~/pages/Compliance/flags";
import JointProfileForm from "~/pages/Compliance/profile/JointProfileForm";
import {
  isIndividualAccountType,
  isJointAccountType,
} from "~/pages/Compliance/utils/accountTypeUtils";
import EditEntityForm from "../EditEntityForm";
import EditIndividualForm from "../EditIndividualForm";

interface Props {
  caseId: number;
  programId: number;
}

export const EditProfileDetailsFullscreenTakeover = ({
  caseId,
  programId: defaultProgramId,
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const guidedProfileCreation = useGuidedProfileCreation();
  const searchParams = new URLSearchParams(location.search);
  const profileId = Number(searchParams.get("profileId"));
  const hasProfileId = Number.isInteger(profileId) && profileId > 0;
  const positionId = searchParams.get("positionId");
  // programId is also Int!, and "?programId=abc" is truthy, so parseInt sent a NaN
  // that serializes to null. Fall back to the case program instead.
  const urlProgramId = Number(searchParams.get("programId"));
  const programId =
    Number.isInteger(urlProgramId) && urlProgramId > 0 ? urlProgramId : defaultProgramId;

  const profileDetailsResults = useGetProfileDetailsQuery({
    variables: {
      profileId: profileId,
      programId: programId,
    },
    skip: !hasProfileId,
    fetchPolicy: "no-cache",
  });

  const profilesUrl = `${globalEnv.complianceRoute}/case/${caseId}/profiles`;

  const onExit = () => {
    history.push(profilesUrl);
  };

  // The route matches on path, so it mounts without the param. Skipping alone
  // renders an empty form, so leave. replace keeps the bad URL out of history.
  useEffect(() => {
    if (!hasProfileId) {
      history.replace(profilesUrl);
    }
  }, [hasProfileId, history, profilesUrl]);

  // Skipping the query leaves QueryLoader with no data, which renders an empty
  // form. Render nothing at all while the redirect above runs.
  if (!hasProfileId) {
    return null;
  }

  const profile = profileDetailsResults.data?.complianceProfile ?? undefined;
  const isIndividualProfile =
    profile?.complianceObjectType === ComplianceObjectType.Individual ||
    (guidedProfileCreation ? isIndividualAccountType(profile?.accountType) : false);
  const isJointProfile =
    profile?.complianceObjectType === ComplianceObjectType.Joint ||
    (guidedProfileCreation ? isJointAccountType(profile?.accountType) : false);
  const isRootProfile = profile?.isInvestingProfile ?? false;

  const resolveTitle = () => {
    if (isIndividualProfile) return "Edit Individual";
    if (isJointProfile) return "Edit Joint Ownership";
    return "Edit Entity";
  };

  const renderForm = () => {
    if (isIndividualProfile) {
      return (
        <EditIndividualForm
          caseId={caseId}
          complianceProfile={profile}
          programId={programId}
          positionId={positionId}
          onCancel={onExit}
          onCompleted={onExit}
        />
      );
    }

    if (isJointProfile) {
      return (
        <JointProfileForm caseId={caseId} complianceProfile={profile} onCompleted={onExit} />
      );
    }

    return (
      <EditEntityForm
        caseId={caseId}
        complianceProfile={profile}
        programId={programId}
        positionId={positionId}
        onCancel={onExit}
        onCompleted={onExit}
      />
    );
  };

  return (
    <FullscreenTakeover>
      <QueryLoader result={profileDetailsResults}>
        <FullscreenTakeover.Header
          key={profile?.complianceObjectType || "loading"}
          onExit={onExit}
          title={resolveTitle()}
        />
        {renderForm()}
      </QueryLoader>
    </FullscreenTakeover>
  );
};
